<template>
  <div>
    <BlBackdrop
      title="Create new subscription"
      label="Create subscription"
      :disabled="isDisabledSubmit"
      :submitLoading="submitLoading"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onSubmit"
    >
      <div
        class="flex flex-col gap-4 overflow-y-auto"
        :style="{ width: '40rem', maxHeight: 'calc(100vh - 14rem)' }"
      >
        <div class="grid grid-cols-6 gap-4 px-8">
          <div class="grid grid-cols-2 col-span-6 gap-4">
            <div
              v-for="opt in typeOptions"
              :key="opt.value"
              class="px-5 py-5 text-center transition-all duration-300 rounded-lg cursor-pointer"
              :style="{ borderWidth: '1.5px' }"
              :class="[
                typeInput === opt.value
                  ? 'border-light-purple'
                  : 'border-slate-400'
              ]"
              @click="typeInput = opt.value"
            >
              <BlTextSubtitle3
                :text="opt.label"
                :class="[
                  typeInput === opt.value
                    ? 'text-light-purple'
                    : 'text-slate-700'
                ]"
              />
            </div>
          </div>
          <BlInputText
            v-if="typeInput === 'Subscription::Custom'"
            class="col-span-6"
            :input="nameInput"
            @changed="inputHandler"
          />
          <div
            v-if="typeInput === 'Subscription::Custom'"
            class="relative col-span-3"
          >
            <BlInputText :input="amountInput" @changed="inputHandler" />
            <div
              class="absolute bottom-0.5 right-0.5 px-0.5 py-0.5 bg-slate-200 border-l border-slate-400"
            >
              <BlSelectBox
                class="bg-slate-200"
                dense
                borderless
                v-model="currencyInput"
                :options="currencyOptions"
                @change="currencyInput = $event"
              />
            </div>
          </div>
          <BlInputText
            :class="{
              'col-span-3': typeInput === 'Subscription::Custom',
              'col-span-6': typeInput === 'Subscription::FreeTrial'
            }"
            :input="seatsInput"
            @changed="inputHandler"
          />
          <BlInputDate
            class="col-span-3"
            :input="startDateInput"
            @changed="inputHandler"
          />
          <BlInputDate
            class="col-span-3"
            :input="expirationDateInput"
            @changed="inputHandler"
          />
          <BlInputText
            class="col-span-6"
            :input="notesInput"
            @changed="inputHandler"
          />
        </div>

        <div class="flex-shrink-0 h-5 bg-slate-200"></div>
        <div class="flex flex-col gap-4 px-8">
          <div class="flex flex-col gap-2">
            <BlTextSubtitle1 text="Manage features" />
            <BlTextBody2
              text="The features will automatically adjust based on the settings configured when this subscription becomes active."
            />
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex">
              <div
                class="flex items-center gap-2 cursor-pointer"
                @click="analyticsFeature = !analyticsFeature"
              >
                <BlInputToggle
                  :checked="analyticsFeature"
                  @changed="analyticsFeature = !analyticsFeature"
                />
                <BlTextBody2 text="Analytics" />
              </div>
            </div>
            <div class="flex">
              <div
                class="flex items-center gap-2 cursor-pointer"
                @click="appointmentsFeature = !appointmentsFeature"
              >
                <BlInputToggle
                  :checked="appointmentsFeature"
                  @changed="appointmentsFeature = !appointmentsFeature"
                />
                <BlTextBody2 text="Availabilities & appointments" />
              </div>
            </div>
            <div class="flex">
              <div
                class="flex items-center gap-2 cursor-pointer"
                @click="branchesFeature = !branchesFeature"
              >
                <BlInputToggle
                  :checked="branchesFeature"
                  @changed="branchesFeature = !branchesFeature"
                />
                <BlTextBody2 text="Branches" />
              </div>
            </div>
            <div class="flex">
              <div
                class="flex items-center gap-2 cursor-pointer"
                @click="salesPipelinesFeature = !salesPipelinesFeature"
              >
                <BlInputToggle
                  :checked="salesPipelinesFeature"
                  @changed="salesPipelinesFeature = !salesPipelinesFeature"
                />
                <BlTextBody2 text="Sales pipeline" />
              </div>
            </div>
            <div class="flex">
              <div
                class="flex items-center gap-2 cursor-pointer"
                @click="sbtFeature = !sbtFeature"
              >
                <BlInputToggle
                  :checked="sbtFeature"
                  @changed="sbtFeature = !sbtFeature"
                />
                <BlTextBody2 text="Skill-based Treatment (SBT)" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="overlappedOpened"
      title="Subscription period overlap detected"
      label="Update and create subscription"
      :disabled="isDisabledSubmit"
      :submitLoading="submitLoading"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onSubmit"
    >
      <div class="flex flex-col gap-3 px-8" :style="{ width: '40rem' }">
        <div>
          <BlTextBody2 text="The subscription period for" />
          <BlTextBody2 :text="nameInput.model" class="font-bold" />
          <BlTextBody2 text="overlaps with the current active period for" />
          <BlTextBody2
            :text="`${currentSubscription.name}`"
            class="font-bold"
          />
          <BlTextBody2
            :text="
              `(${displayDate({
                date: currentSubscription.start_date
              })} - ${displayDate({
                date: currentSubscription.expiration_date
              })}).`
            "
            class="font-bold"
          />
          <BlTextBody2 text="Please select how you'd like to resolve this:" />
        </div>
        <div class="flex gap-2">
          <BlInputRadio
            name="shorten_current_subscription_period"
            :checked="shortenCurrentSubscriptionPeriod"
            @changed="shortenCurrentSubscriptionPeriod = true"
          />
          <div class="flex flex-col gap-1">
            <BlTextBody2
              text="Shorten current subscription period"
              class="font-bold"
            />
            <div>
              <BlTextBody2 text="The current active subscription" />
              <BlTextBody2
                :text="`(${currentSubscription.name})`"
                class="font-bold"
              />
              <BlTextBody2 text="will end early on" />
              <BlTextBody2
                :text="displayDate({ date: startDateInput.model })"
                class="font-bold"
              />
              <BlTextBody2 text="to accommodate the new period." />
            </div>
          </div>
        </div>
        <div class="flex gap-2">
          <BlInputRadio
            name="shorten_current_subscription_period"
            :checked="!shortenCurrentSubscriptionPeriod"
            @changed="shortenCurrentSubscriptionPeriod = false"
          />
          <div class="flex flex-col gap-1">
            <BlTextBody2
              text="Adjust new subscription start date"
              class="font-bold"
            />
            <div>
              <BlTextBody2
                text="Change the start date of the new subscription"
              />
              <BlTextBody2 :text="`(${nameInput.model})`" class="font-bold" />
              <BlTextBody2
                text="to prevent overlap with the existing active period."
              />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <BlInputDate
                :input="overlappedStartDateInput"
                @changed="inputHandler"
              />
              <BlInputDate
                :input="overlappedExpirationDateInput"
                @changed="inputHandler"
              />
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import moment from "moment";

import BlBackdrop from "../../../BlBackdrop.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlTextSubtitle1 from "../../../BlTextSubtitle1.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlInputText from "../../../BlInputText.vue";
import BlInputDate from "../../../BlInputDate.vue";
import BlInputToggle from "../../../BlInputToggle.vue";
import BlInputRadio from "../../../BlInputRadio.vue";
import BlSelectBox from "../../../BlSelectBox.vue";

export default {
  name: "NewSubscriptionModal",
  components: {
    BlBackdrop,
    BlTextSubtitle3,
    BlTextSubtitle1,
    BlTextBody2,
    BlInputText,
    BlInputDate,
    BlInputToggle,
    BlInputRadio,
    BlSelectBox
  },
  props: {
    // @close-modal
  },
  data() {
    return {
      submitLoading: false,
      //
      typeInput: "Subscription::Custom",
      typeOptions: [
        { value: "Subscription::Custom", label: "Custom subscription" },
        { value: "Subscription::FreeTrial", label: "Free trial" }
      ],
      nameInput: {
        label: "Subscription name",
        placeholder: "Type name",
        asterisk: true,
        model: ""
      },
      amountInput: {
        label: "Total amount due",
        placeholder: "Type amount",
        asterisk: true,
        model: "0",
        prefix: "Rp",
        prefixSpace: "3rem"
      },
      currencyInput: "IDR",
      currencyOptions: [
        { value: "IDR", label: "IDR", symbol: "Rp" },
        { value: "USD", label: "USD", symbol: "$" },
        { value: "SGD", label: "SGD", symbol: "$" },
        { value: "MYR", label: "MYR", symbol: "RM" },
        { value: "PHP", label: "PHP", symbol: "₱" },
        { value: "THB", label: "THB", symbol: "฿" },
        { value: "VND", label: "VND", symbol: "₫" }
      ],
      seatsInput: {
        label: "Number of seats",
        placeholder: "Type number",
        asterisk: true,
        inputType: "number",
        model: "",
        suffix: "seat(s)"
      },
      startDateInput: {
        label: "Subscription start date",
        asterisk: true,
        model: ""
      },
      expirationDateInput: {
        label: "Subscription expiry date",
        asterisk: true,
        model: ""
      },
      notesInput: {
        label: "Notes",
        placeholder: "Type notes",
        textarea: true,
        model: ""
      },
      //
      overlappedStartDateInput: {
        label: "Subscription start date",
        asterisk: true,
        model: ""
      },
      overlappedExpirationDateInput: {
        label: "Subscription expiry date",
        asterisk: true,
        model: ""
      },
      //
      branchesFeature: false,
      analyticsFeature: false,
      salesPipelinesFeature: false,
      appointmentsFeature: false,
      sbtFeature: false,
      //
      overlappedOpened: false,
      shortenCurrentSubscriptionPeriod: false,
      currentSubscription: { id: 0, name: "" }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "center"]),
    isDisabledSubmit() {
      if (this.typeInput === "Subscription::Custom") {
        if (!this.nameInput.model) return true;
        if (!this.amountInput.model) return true;
      }
      if (!this.seatsInput.model) return true;
      if (!this.overlappedOpened) {
        if (!this.startDateInput.model) return true;
        if (!this.expirationDateInput.model) return true;
      }
      if (this.overlappedOpened) {
        if (!this.shortenCurrentSubscriptionPeriod) {
          if (!this.overlappedStartDateInput.model) return true;
          if (!this.overlappedExpirationDateInput.model) return true;
        }
      }
      return false;
    }
  },
  watch: {
    currencyInput(val) {
      if (!val) return;
      const opt = this.currencyOptions.find(i => i.value === val);
      this.amountInput.prefix = opt.symbol;
    }
  },
  mounted() {},
  methods: {
    ...mapActions(["createSubscription"]),
    displayDate,
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async onSubmit() {
      if (this.typeInput === "Subscription::FreeTrial") {
        this.nameInput.model = "Free trial";
      }
      const params = {
        center_id: this.center.id,
        subscription: {
          name: this.nameInput.model,
          amount: Number(this.amountInput.model),
          currency: this.currencyInput,
          number_of_seats: Number(this.seatsInput.model),
          start_date: this.startDateInput.model,
          expiration_date: this.expirationDateInput.model,
          type: this.typeInput,
          notes: this.notesInput.model,
          accessible_features: {
            branches: this.branchesFeature,
            analytics: this.analyticsFeature,
            sales_pipelines: this.salesPipelinesFeature,
            availabilities_and_appointments: this.appointmentsFeature,
            sbt: this.sbtFeature
          }
        }
      };

      if (this.overlappedOpened) {
        if (this.shortenCurrentSubscriptionPeriod) {
          params.current_subscription_id = this.currentSubscription.id;
        }
        if (!this.shortenCurrentSubscriptionPeriod) {
          params.subscription.start_date = this.overlappedStartDateInput.model;
          params.subscription.expiration_date = this.overlappedExpirationDateInput.model;
        }
      }

      this.submitLoading = true;
      const { success, data, message } = await this.createSubscription(params);
      this.submitLoading = false;
      if (!success) {
        if (message.includes("period overlaps")) {
          const start = moment(data.expiration_date)
            .add(1, "days")
            .format("YYYY-MM-DD");
          this.overlappedStartDateInput.min = start;
          this.overlappedStartDateInput.model = start;
          this.overlappedExpirationDateInput.min = start;
          this.overlappedExpirationDateInput.model = this.expirationDateInput.model;
          this.overlappedOpened = true;
          this.currentSubscription = data;
        }
        return;
      }

      if (this.overlappedOpened) this.$emit("after-commit");
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/center/subscription/NewSubscriptionModal.vue